import { useHasFeature } from '@/design-system/components/hasFeature/useHasFeature.hook';
import { popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { UniTabHeader } from '@/design-system/components/tabs';
import { TextTitle } from '@/design-system/components/typography/UniTypography';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { keyGenerator } from '@/utils/keyGenerator';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { FlatList, Pressable, ScrollView, SxProp, View } from 'dripsy';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackRoutes } from 'types';
import { PAYMENT_TYPE } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { onGoBack } from '../../utils/utils';
import { HeaderBackgroundImageComponent } from '../components/HeaderBackgroundImageComponent';
import { HelpCenterComponent } from '../components/HelpCenter.component';
import { ItemPayment } from '../components/ItemPayment';
import { LandingItem } from './BillPaymentLandingWeb.component';

export interface BillPaymentLandingNativeProps {}

export const BillPaymentLandingNativeComponent: React.FC<BillPaymentLandingNativeProps> = () => {
  const { t } = useTranslation('global-bill-payment');
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();

  const paymentTypes: LandingItem[] = [
    {
      name: 'Local Bill Payment',
      iconName: '',
      title: t('global-bill-payment::glossary.title--local-bill-payment'),
      description: t('global-bill-payment::landing.text--local-bill-payment'),
      parentScreenName: RootStackRoutes.BillPayment,
      childScreenName: BillPaymentStackRoutes.Instruction,
      params: {
        paymentType: PAYMENT_TYPE.LOCAL
      },
      showFlag: useHasFeature({ feature: 'BILL_PAYMENT', variation: 'beta' })?.enabled
    },
    {
      name: 'Oversea Bill Payment',
      iconName: '',
      title: t('global-bill-payment::glossary.title--overseas-bill-payment'),
      description: t('global-bill-payment::landing.text--overseas-bill-payment'),
      parentScreenName: RootStackRoutes.BillPayment,
      childScreenName: BillPaymentStackRoutes.Instruction,
      params: {
        paymentType: PAYMENT_TYPE.GLOBAL
      },
      showFlag: useHasFeature({ feature: 'BILL_PAYMENT', variation: 'beta' })?.enabled
    },
    {
      name: 'Maintenance',
      iconName: '',
      title: t('global-bill-payment::landing.title--maintenance'),
      description: t('global-bill-payment::landing.text--bp-maintenance'),
      showFlag:
        useHasFeature({ feature: 'DIGITAL_WITHDRAW', variation: 'enabled' })?.enabled ||
        useHasFeature({ feature: 'FIAT_WITHDRAW', variation: 'enabled' })?.enabled
    },
    {
      name: 'UnionPay',
      iconName: '',
      title: t('global-bill-payment::landing.title--union-pay'),
      description: t('global-bill-payment::landing.text--bp-union-pay'),
      parentScreenName: RootStackRoutes.BillPayment,
      childScreenName: BillPaymentStackRoutes.UnionPay,
      showFlag: useHasFeature({ feature: 'UNIONPAY_REMITTANCE', variation: 'beta' })?.enabled
    }
    // {
    //   name: 'Acquisition',
    //   iconName: '',
    //   title: t('global-bill-payment::general.acquisition'),
    //   description: t('global-bill-payment::landing.text--bp-acquisition')
    // },
    // {
    //   name: 'Investment',
    //   iconName: '',
    //   title: t('global-bill-payment::landing.title--investment'),
    //   description: t('global-bill-payment::landing.text--bp-investment')
    // }
  ];

  const transferTypes: LandingItem[] = [
    {
      name: 'General Transfer',
      iconName: '',
      title: t('global-bill-payment::landing.title--general-transfer'),
      description: t('global-bill-payment::landing.text--general-transfer'),
      showFlag: useHasFeature({ feature: 'TRANSFER', variation: 'enabled' })?.enabled
    },
    {
      name: 'Exchange Transfer',
      iconName: '',
      title: t('global-bill-payment::landing.title--bp-transfer'),
      description: t('global-bill-payment::landing.text--bp-transfer'),
      showFlag: useHasFeature({ feature: 'EXCHANGE_SUB_ACCOUNT', variation: 'enabled' })?.enabled
    }
  ];

  const onNavigate = (item: LandingItem) => {
    if (item.parentScreenName && item.childScreenName) {
      navigation.navigate(item.parentScreenName, {
        screen: item.childScreenName,
        params: item.params
      });
    }
    switch (item.name) {
      case 'Maintenance':
        return iframePostMessage('showMaintenance', true);
      case 'General Transfer':
        return iframePostMessage('showTransfer', true);
      default:
        return null;
    }
  };

  const onPopState = (event: any) => {
    window.history.pushState(event.state, '');
    setTimeout(() => {
      const popStateLocked = popStateLockedMap.get('popstate') ?? false;
      if (!document.location.href || popStateLocked) return;
      iframePostMessage('backToOverview', true);
    }, 200);
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  const onBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      window.removeEventListener('popstate', onPopState);
      onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      onGoBack(navigation);
    }
  };

  return (
    <View sx={sxStyles.container}>
      <View sx={{ ...sxStyles.header, ...{ pt: insets.top } }}>
        <HeaderBackgroundImageComponent />
        <UniTabHeader
          title={t('global-bill-payment::general.payment-instruction')}
          showBackButton={navigation.canGoBack()}
          onBack={() => onBack()}
          labelColor="$white"
          sx={sxStyles.tab}
        />
      </View>
      <ScrollView sx={sxStyles.scrollViewWrapper} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          {(useHasFeature({ feature: 'BILL_PAYMENT', variation: 'beta' })?.enabled ||
            useHasFeature({ feature: 'UNIONPAY_REMITTANCE', variation: 'beta' })?.enabled) && (
            <TextTitle semiBold sx={sxStyles.textPayment}>
              {t('global-bill-payment::glossary.title--payment')}
            </TextTitle>
          )}
          <FlatList
            sx={sxStyles.flatListWrapper}
            data={paymentTypes}
            horizontal
            keyExtractor={() => keyGenerator('payments')}
            renderItem={({ item }) => {
              const landingItem = item as LandingItem;
              if (landingItem?.showFlag == false) return null;
              return (
                <Pressable onPress={() => onNavigate(landingItem)}>
                  <ItemPayment payment={landingItem} />
                </Pressable>
              );
            }}
            ListHeaderComponent={() => <View sx={sxStyles.footerSeparator} />}
            ListFooterComponent={() => <View sx={sxStyles.footerSeparator} />}
            ItemSeparatorComponent={() => <View sx={sxStyles.dividerSeparator} />}
            showsHorizontalScrollIndicator={false}
          />
          {(useHasFeature({ feature: 'TRANSFER', variation: 'enabled' })?.enabled ||
            useHasFeature({ feature: 'EXCHANGE_SUB_ACCOUNT', variation: 'enabled' })?.enabled) && (
            <TextTitle semiBold sx={sxStyles.textPayment}>
              {t('global-bill-payment::glossary.title--transfer')}
            </TextTitle>
          )}
          <FlatList
            sx={sxStyles.flatListWrapper}
            data={transferTypes}
            horizontal
            keyExtractor={() => keyGenerator('transfer')}
            renderItem={({ item }) => {
              const landingItem = item as LandingItem;
              if (landingItem?.showFlag == false) return null;
              return (
                <Pressable onPress={() => onNavigate(landingItem)}>
                  <ItemPayment payment={landingItem} />
                </Pressable>
              );
            }}
            ListHeaderComponent={() => <View sx={sxStyles.footerSeparator} />}
            ListFooterComponent={() => <View sx={sxStyles.footerSeparator} />}
            ItemSeparatorComponent={() => <View sx={sxStyles.dividerSeparator} />}
            showsHorizontalScrollIndicator={false}
          />
          <HelpCenterComponent sx={sxStyles.viewHelpCenter} />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  header: {
    flexDirection: 'column'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    flex: 1,
    zIndex: 10
  },
  scrollViewWrapper: {
    pt: '$5',
    pb: '$10'
  },
  textPayment: {
    px: '$4'
  },
  footerSeparator: {
    width: '$4'
  },
  dividerSeparator: {
    width: '$5'
  },
  flatListWrapper: {
    flexGrow: 0,
    pt: '$3',
    pb: '$5'
  },
  viewHelpCenter: {
    ml: '$4',
    mr: '$1',
    mt: '$5'
  }
};
